import { useMemo, useState } from 'react'
import { usePlayerStore } from '../stores'
import { useBlogFixtureStore } from '../stores/useBlogFixtureStore'
import { Pencil, Trash2 } from 'lucide-react'
import { collectCompetitionNames } from '../utils/competitions'

function AdminFixtures() {
  const fixtures = useBlogFixtureStore((state) => state.fixtures)
  const createFixture = useBlogFixtureStore((state) => state.createFixture)
  const updateFixture = useBlogFixtureStore((state) => state.updateFixture)
  const deleteFixture = useBlogFixtureStore((state) => state.deleteFixture)
  const { clubs, competitions } = usePlayerStore()
  const competitionOptions = useMemo(
    () => collectCompetitionNames({ competitions, clubs }),
    [clubs, competitions]
  )

  const [fixtureForm, setFixtureForm] = useState({
    id: '',
    date: new Date().toISOString().split('T')[0],
    competition: '',
    home_team: '',
    away_team: '',
    location: '',
    status: 'upcoming' as 'upcoming' | 'played',
    score: '',
  })
  const [editingFixtureId, setEditingFixtureId] = useState<string | null>(null)
  const [customCompetitionName, setCustomCompetitionName] = useState('')
  const [saveError, setSaveError] = useState('')

  const resetFixtureForm = () => {
    setEditingFixtureId(null)
    setCustomCompetitionName('')
    setFixtureForm({
      id: '',
      date: new Date().toISOString().split('T')[0],
      competition: '',
      home_team: '',
      away_team: '',
      location: '',
      status: 'upcoming',
      score: '',
    })
  }

  const handleSaveFixture = async () => {
    const competitionName = customCompetitionName.trim() || fixtureForm.competition.trim()
    if (!competitionName || !fixtureForm.home_team.trim() || !fixtureForm.away_team.trim()) {
      setSaveError('Competition and both team names are required.')
      return
    }
    if (fixtureForm.status === 'played' && !/^\s*\d+\s*[-:]\s*\d+\s*$/.test(fixtureForm.score)) {
      setSaveError('Enter a score in the format 2-1.')
      return
    }
    setSaveError('')

    const fixturePayload = {
      id: editingFixtureId || crypto.randomUUID(),
      date: fixtureForm.date,
      competition: competitionName,
      home_team: fixtureForm.home_team.trim(),
      away_team: fixtureForm.away_team.trim(),
      location: fixtureForm.location.trim(),
      status: fixtureForm.status,
      score: fixtureForm.status === 'played' ? fixtureForm.score.trim() : undefined,
    }

    try {
      if (editingFixtureId) {
        await updateFixture(fixturePayload)
      } else {
        await createFixture(fixturePayload)
      }
      resetFixtureForm()
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save fixture.')
    }
  }

  const handleEditFixture = (fixtureId: string) => {
    const fixture = fixtures.find((item) => item.id === fixtureId)
    if (!fixture) return
    setEditingFixtureId(fixtureId)
    setCustomCompetitionName(competitionOptions.includes(fixture.competition) ? '' : fixture.competition)
    setFixtureForm({
      id: fixture.id,
      date: fixture.date,
      competition: fixture.competition,
      home_team: fixture.home_team,
      away_team: fixture.away_team,
      location: fixture.location,
      status: fixture.status,
      score: fixture.score || '',
    })
    if (!competitionOptions.includes(fixture.competition)) {
      setFixtureForm((prev) => ({ ...prev, competition: '' }))
    }
  }

  const handleDeleteFixture = async (fixtureId: string) => {
    setSaveError('')
    try {
      await deleteFixture(fixtureId)
      if (editingFixtureId === fixtureId) {
        resetFixtureForm()
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to delete fixture.')
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.28em] text-cyan-400">Fixtures & results</p>
        <h2 className="mt-2 text-2xl font-black text-white">Match board</h2>
        <p className="mt-1 text-sm text-slate-400">Manage the public fixtures and results board.</p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-6 rounded-[30px] border border-cyan-500/20 bg-gradient-to-br from-[#0d1117] via-[#0d121d] to-[#0f1d25] p-6 shadow-[0_0_0_1px_rgba(34,211,238,0.08)]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.28em] text-cyan-400">Fixture editor</p>
              <h3 className="mt-2 text-2xl font-black text-white">{editingFixtureId ? 'Edit fixture' : 'Add fixture'}</h3>
            </div>
            <div className="rounded-full border border-cyan-500/20 bg-cyan-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.25em] text-cyan-300">
              {editingFixtureId ? 'Edit' : 'New'}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm text-slate-200">
              Date
              <input
                type="date"
                value={fixtureForm.date}
                onChange={(e) => setFixtureForm((prev) => ({ ...prev, date: e.target.value }))}
                className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-500"
              />
            </label>
            <label className="block text-sm text-slate-200">
              Competition
              <select
                value={competitionOptions.includes(fixtureForm.competition) ? fixtureForm.competition : ''}
                onChange={(e) => {
                  setFixtureForm((prev) => ({ ...prev, competition: e.target.value }))
                  setCustomCompetitionName('')
                }}
                className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-500"
              >
                <option value="">Select competition</option>
                {competitionOptions.map((competition) => (
                  <option key={competition} value={competition}>{competition}</option>
                ))}
              </select>
              <input
                value={customCompetitionName}
                onChange={(e) => {
                  setCustomCompetitionName(e.target.value)
                  setFixtureForm((prev) => ({ ...prev, competition: '' }))
                }}
                className="mt-3 w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-500"
                placeholder="Or type a custom league"
              />
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm text-slate-200">
              Home Team
              <input
                value={fixtureForm.home_team}
                onChange={(e) => setFixtureForm((prev) => ({ ...prev, home_team: e.target.value }))}
                className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-500"
                placeholder="Home team"
              />
            </label>
            <label className="block text-sm text-slate-200">
              Away Team
              <input
                value={fixtureForm.away_team}
                onChange={(e) => setFixtureForm((prev) => ({ ...prev, away_team: e.target.value }))}
                className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-500"
                placeholder="Away team"
              />
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm text-slate-200">
              Location
              <input
                value={fixtureForm.location}
                onChange={(e) => setFixtureForm((prev) => ({ ...prev, location: e.target.value }))}
                className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-500"
                placeholder="Stadium or city"
              />
            </label>
            <label className="block text-sm text-slate-200">
              Status
              <select
                value={fixtureForm.status}
                onChange={(e) => setFixtureForm((prev) => ({ ...prev, status: e.target.value as 'upcoming' | 'played' }))}
                className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-500"
              >
                <option value="upcoming">Upcoming</option>
                <option value="played">Played</option>
              </select>
            </label>
          </div>

          {fixtureForm.status === 'played' && (
            <label className="block text-sm text-slate-200">
              Score
              <input
                value={fixtureForm.score}
                onChange={(e) => setFixtureForm((prev) => ({ ...prev, score: e.target.value }))}
                className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-500"
                placeholder="2-1"
              />
            </label>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void handleSaveFixture()}
              className="rounded-xl bg-cyan-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-cyan-500"
            >
              {editingFixtureId ? 'Save Fixture' : 'Add Fixture'}
            </button>
            {editingFixtureId && (
              <button
                type="button"
                onClick={resetFixtureForm}
                className="rounded-xl bg-slate-800 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
              >
                Cancel Edit
              </button>
            )}
          </div>
          {saveError && <p role="alert" className="text-sm text-red-400">{saveError}</p>}
        </div>

        <div className="rounded-[30px] border border-slate-800 bg-[#0d1117] p-6">
          <h3 className="text-xl font-black text-white">Fixture list</h3>
          <div className="mt-5 space-y-3">
            {fixtures.length === 0 ? (
              <p className="text-sm text-slate-400">No fixtures configured yet.</p>
            ) : (
              fixtures.map((fixture) => (
                <div key={fixture.id} className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-400">{fixture.competition}</p>
                      <p className="mt-2 text-sm text-slate-300">{fixture.date} · {fixture.location || 'Location TBD'}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditFixture(fixture.id)}
                        className="rounded-xl border border-slate-700 bg-slate-900 p-2 text-slate-200 hover:border-cyan-500 hover:text-white"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDeleteFixture(fixture.id)}
                        className="rounded-xl border border-slate-700 bg-slate-900 p-2 text-red-400 hover:border-red-500 hover:text-white"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <p className="mt-4 text-base font-bold text-white">
                    {fixture.home_team} {fixture.status === 'played' ? fixture.score || 'vs' : 'vs'} {fixture.away_team}
                  </p>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.2em] ${
                      fixture.status === 'played'
                        ? 'bg-emerald-500/10 text-emerald-300'
                        : 'bg-cyan-500/10 text-cyan-300'
                    }`}>
                      {fixture.status}
                    </span>
                    {fixture.status === 'played' && fixture.score && (
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">Result</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminFixtures
